import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Switch } from 'react-native';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import Animated, { FadeIn, FadeInDown, FadeOut, LinearTransition } from 'react-native-reanimated';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  FileText,
  Layers,
  MessageSquare,
  MessagesSquare,
  Mic,
  NotebookPen,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserCircle2,
  Users,
} from 'lucide-react-native';
import { AppLayout, type Breadcrumb } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppModal, ModalAction } from '@/components/modal';
import { AccountActionButton } from '@/components/account/AccountActionButton';
import { StepUpModal } from '@/components/account/StepUpModal';
import { useAlert } from '@/components/AlertDialog';
import { useThemeColors } from '@/lib/useThemeColors';
import { useDesktopShell } from '@/lib/useShell';
import { isWeb } from '@/lib/platform';
import { useAuth } from '@/lib/supabase';
import { useUserRole } from '@/lib/auth/role';
import { handleError } from '@/lib/errors';
import { isDemoMode } from '@/lib/config/app-mode';
import { useDataroomStore } from '@/lib/state/dataroom-store';
import {
  ManageDataError,
  categoryMeta,
  companyDataLine,
  formatItemDate,
  manageDataAvailable,
  useDataItems,
  useDataSummary,
  useDeleteDataItems,
  useFounderCompany,
  useSetAnalyticsOptOut,
  visibleCategories,
  type CategoryMeta,
  type CompanyDataLine,
  type DataCategory,
  type DataItem,
  type DeleteTarget,
  type LocalEffects,
} from '@/lib/gdpr/manage-data';

const MAX_WIDTH = 680;

const ICONS: Record<DataCategory, React.ComponentType<{ size: number; color: string }>> = {
  data_room_document: FileText,
  deck_analysis: Layers,
  dd_conversation: MessagesSquare,
  dd_note: NotebookPen,
  feedback: MessageSquare,
  analytics_events: BarChart3,
  avatar: UserCircle2,
};

/** One pending confirmation. "Delete all" walks two steps. */
interface Confirm {
  category: DataCategory;
  target: DeleteTarget;
  shownIds: string[];
  title: string;
  body: string;
  step: 1 | 2;
}

/** What the step-up, once passed, should retry. */
type Pending = { kind: 'load' } | { kind: 'delete'; confirm: Confirm } | { kind: 'analytics'; optOut: boolean };

/**
 * Manage my data — GDPR Art. 17, granular. Delete specific pieces of your own
 * data without closing the account (Settings → Your Data).
 *
 * The server decides what is yours (data-item-delete): this screen shows only
 * what it lists, so it never offers a deletion the server would refuse. A
 * co-run company's documents are team data and are explained, not offered.
 */
export default function ManageDataScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const desktop = useDesktopShell();
  const { user, refreshProfile } = useAuth();
  const { data: role } = useUserRole(user?.id);
  const { alert } = useAlert();
  const removeDocs = useDataroomStore((s) => s.setDataRoomDocuments);

  const available = manageDataAvailable() && !isDemoMode();
  const [open, setOpen] = useState<DataCategory | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [stepUp, setStepUp] = useState<Pending | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const summary = useDataSummary(available ? user?.id : null);
  const items = useDataItems(available ? user?.id : null, open);
  // Company data (interview transcripts) is shown to founders, never offered for deletion.
  const company = useFounderCompany(available ? user?.id : null, role === 'startup');
  const companyLine = company.data ? companyDataLine(company.data.name) : null;

  const effects: LocalEffects = useMemo(
    () => ({
      removeDataRoomDocuments: (ids) => {
        const gone = new Set(ids);
        removeDocs(useDataroomStore.getState().dataRoomDocuments.filter((d) => !gone.has(d.id)));
      },
      refreshProfile,
    }),
    [removeDocs, refreshProfile],
  );
  const del = useDeleteDataItems(user?.id, effects);
  const optOut = useSetAnalyticsOptOut(user?.id);

  const stepUpNeeded =
    (summary.error instanceof ManageDataError && summary.error.kind === 'step_up_required') ||
    (items.error instanceof ManageDataError && items.error.kind === 'step_up_required');

  useEffect(() => {
    if (stepUpNeeded) setStepUp({ kind: 'load' });
  }, [stepUpNeeded]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const categories = useMemo(
    () => visibleCategories(role, summary.data?.counts),
    [role, summary.data?.counts],
  );

  const runDelete = (c: Confirm) => {
    if (del.isPending) return;
    if (!isWeb) Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
    del.mutate(
      { category: c.category, target: c.target, shownIds: c.shownIds },
      {
        onSuccess: (r) => {
          setConfirm(null);
          if (!isWeb) Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          const noun = categoryMeta(c.category).noun;
          setNotice(
            r.deleted === 0
              ? `Nothing to delete — that ${noun} was already gone.`
              : r.inProgress > 0
                ? `Deleted ${r.deleted}. ${r.inProgress} still being analysed — delete it once it finishes.`
                : r.deleted === 1
                  ? `Deleted.`
                  : `Deleted ${r.deleted} items.`,
          );
        },
        onError: (err) => {
          if (err instanceof ManageDataError && err.kind === 'step_up_required') {
            setConfirm(null);
            setStepUp({ kind: 'delete', confirm: c });
            return;
          }
          setConfirm(null);
          if (err instanceof ManageDataError && err.kind === 'storage_incomplete') {
            alert('Deleted, with one loose end', err.userMessage);
            return;
          }
          const { message, isCancelled } = handleError(err, { context: 'ManageData.delete' });
          if (!isCancelled) alert('Not deleted', err instanceof ManageDataError ? err.userMessage : message);
        },
      },
    );
  };

  const toggleAnalytics = (value: boolean) => {
    optOut.mutate(value, {
      onError: (err) => {
        if (err instanceof ManageDataError && err.kind === 'step_up_required') {
          setStepUp({ kind: 'analytics', optOut: value });
          return;
        }
        const { message, isCancelled } = handleError(err, { context: 'ManageData.analyticsOptOut' });
        if (!isCancelled) alert('Not saved', err instanceof ManageDataError ? err.userMessage : message);
      },
    });
  };

  const onStepUpVerified = () => {
    const p = stepUp;
    setStepUp(null);
    if (!p) return;
    if (p.kind === 'load') {
      summary.refetch();
      if (open) items.refetch();
    } else if (p.kind === 'delete') runDelete(p.confirm);
    else toggleAnalytics(p.optOut);
  };

  const askDelete = (category: DataCategory, item: DataItem | null) => {
    const meta = categoryMeta(category);
    if (item) {
      setConfirm({
        category,
        target: { ids: [item.id] },
        shownIds: [item.id],
        title: `Delete this ${meta.noun}?`,
        body: `"${item.title}" is deleted permanently. This cannot be undone.`,
        step: 2,
      });
      return;
    }
    const shownIds = (items.data ?? []).map((i) => i.id);
    const count = summary.data?.counts[category] ?? shownIds.length;
    setConfirm({
      category,
      target: { all: true },
      shownIds,
      title: meta.mode === 'whole' ? `Delete your ${meta.noun}?` : `Delete all ${count} ${meta.label.toLowerCase()}?`,
      body: meta.description,
      // "Delete all" of an item category asks twice; a whole category once, then again.
      step: 1,
    });
  };

  const breadcrumbs: Breadcrumb[] = [
    { label: 'Settings', href: '/settings' },
    { label: 'Manage my data', href: open ? '/manage-data' : undefined },
    ...(open ? [{ label: categoryMeta(open).label }] : []),
  ];

  const openMeta = open ? categoryMeta(open) : null;

  return (
    <AppLayout title="Manage my data" breadcrumbs={breadcrumbs}>
      <ScreenFrame
        title={openMeta ? openMeta.label : 'Manage my data'}
        back={{
          fallback: '/settings',
          label: openMeta ? 'Your data' : 'Settings',
          // In a category, Back returns to the overview instead of leaving.
          onPress: () => {
            if (open) {
              setOpen(null);
              return false;
            }
          },
        }}
        bottomEdge
      >
        <ScrollView
          contentContainerStyle={{ paddingHorizontal: 20, paddingTop: 8, paddingBottom: 56, flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
        >
          <View style={{ width: '100%', maxWidth: MAX_WIDTH, alignSelf: 'center', gap: 18 }}>
            {notice && (
              <Animated.View entering={FadeInDown.duration(220)} exiting={FadeOut.duration(200)}>
                <View
                  className="flex-row items-center"
                  style={{
                    gap: 10,
                    borderRadius: 14,
                    paddingHorizontal: 14,
                    paddingVertical: 12,
                    backgroundColor: colors.primary[50],
                    borderWidth: 1,
                    borderColor: colors.primary[100],
                  }}
                >
                  <View className="shrink-0">
                    <ShieldCheck size={18} color={colors.primary[700]} />
                  </View>
                  <Text numberOfLines={2} className="flex-1" style={{ color: colors.text.primary, fontSize: 14, fontWeight: '600' }}>
                    {notice}
                  </Text>
                </View>
              </Animated.View>
            )}

            {!available ? (
              <Notice
                title="Not available here"
                body={
                  isDemoMode()
                    ? 'Shared demo accounts are used by everyone who tries Inverio, so their data cannot be deleted. Sign in with your own account to manage your data.'
                    : 'Managing your data needs a live account. It is not available in offline sample mode.'
                }
              />
            ) : stepUpNeeded ? (
              <Notice
                title="Confirm it's you first"
                body="Your data is behind your authenticator. Verify to see and delete it."
                action={{ label: 'Enter my code', onPress: () => setStepUp({ kind: 'load' }) }}
              />
            ) : open && openMeta ? (
              <CategoryDetail
                meta={openMeta}
                count={summary.data?.counts[open] ?? 0}
                items={items.data}
                loading={items.isLoading}
                error={items.error && !(items.error instanceof ManageDataError && items.error.kind === 'step_up_required') ? items.error : null}
                onRetry={() => items.refetch()}
                onDelete={(item) => askDelete(open, item)}
                onDeleteAll={() => askDelete(open, null)}
                analyticsOptOut={summary.data?.analyticsOptOut ?? false}
                onToggleAnalytics={toggleAnalytics}
                togglePending={optOut.isPending}
                showInlineBack={desktop}
                onBack={() => setOpen(null)}
                deleting={del.isPending}
              />
            ) : summary.isLoading ? (
              <View className="items-center justify-center" style={{ paddingVertical: 64 }}>
                <ActivityIndicator color={colors.primary[600]} />
              </View>
            ) : summary.error ? (
              <Notice
                title="Couldn't load your data"
                body={summary.error instanceof ManageDataError ? summary.error.userMessage : 'Check your connection and try again.'}
                action={
                  summary.error instanceof ManageDataError && summary.error.kind === 'demo_account'
                    ? undefined
                    : { label: 'Try again', onPress: () => summary.refetch() }
                }
              />
            ) : summary.data ? (
              <Overview
                categories={categories}
                counts={summary.data.counts}
                teamCompanies={summary.data.teamCompanies}
                onOpen={setOpen}
                companyLine={companyLine}
                onRefreshCompanyData={(href) => router.push(href)}
                onExport={() => router.push('/settings')}
              />
            ) : null}
          </View>
        </ScrollView>
      </ScreenFrame>

      <AppModal visible={confirm !== null} onClose={() => (del.isPending ? undefined : setConfirm(null))} title={confirm?.title}>
        <View style={{ gap: 10 }}>
          <Text style={{ color: colors.text.primary, fontSize: 15, lineHeight: 22 }}>
            {confirm?.step === 1
              ? confirm.body
              : confirm && 'all' in confirm.target
                ? 'Last check: everything in this category is deleted permanently. This cannot be undone.'
                : confirm?.body}
          </Text>
          {confirm?.category === 'data_room_document' && (
            <Text style={{ color: colors.text.tertiary, fontSize: 13, lineHeight: 19 }}>
              A file synced from Google Drive or Dropbox comes back on the next sync unless you remove it there too.
            </Text>
          )}
          {del.isPending && <ActivityIndicator color={colors.error} />}
        </View>
        <AppModal.Actions>
          <ModalAction label="Cancel" onPress={() => setConfirm(null)} disabled={del.isPending} />
          {confirm?.step === 1 ? (
            <ModalAction
              label="Continue"
              variant="destructive"
              onPress={() => setConfirm((c) => (c ? { ...c, step: 2 } : c))}
            />
          ) : (
            <ModalAction
              label={confirm && 'all' in confirm.target ? 'Delete all' : 'Delete'}
              variant="destructive"
              icon={<Trash2 size={16} color={colors.text.inverse} />}
              disabled={del.isPending}
              onPress={() => confirm && runDelete(confirm)}
            />
          )}
        </AppModal.Actions>
      </AppModal>

      <StepUpModal
        visible={stepUp !== null}
        reason={stepUp?.kind === 'delete' ? 'delete your data' : 'see and manage your data'}
        onClose={() => setStepUp(null)}
        onVerified={onStepUpVerified}
      />
    </AppLayout>
  );
}

// -----------------------------------------------------------------------------
// Overview
// -----------------------------------------------------------------------------

function Overview({
  categories,
  counts,
  teamCompanies,
  onOpen,
  companyLine,
  onRefreshCompanyData,
  onExport,
}: {
  categories: CategoryMeta[];
  counts: Record<DataCategory, number>;
  teamCompanies: { startupId: string; name: string }[];
  onOpen: (c: DataCategory) => void;
  companyLine: CompanyDataLine | null;
  onRefreshCompanyData: (href: CompanyDataLine['href']) => void;
  onExport: () => void;
}) {
  const colors = useThemeColors();
  const total = categories.reduce((n, c) => n + (counts[c.key] ?? 0), 0);

  return (
    <>
      <Animated.View entering={FadeInDown.duration(350)} style={{ gap: 6 }}>
        <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 28, fontWeight: '700', letterSpacing: -0.5 }}>
          Your data, piece by piece
        </Text>
        <Text numberOfLines={4} style={{ color: colors.text.secondary, fontSize: 15, lineHeight: 22 }}>
          Delete specific things without closing your account. Deleted means gone — from the app and from our storage.
        </Text>
      </Animated.View>

      {/* The tally — what is stored about you right now. */}
      <Animated.View
        entering={FadeInDown.delay(60).duration(350)}
        style={{
          borderRadius: 20,
          padding: 20,
          backgroundColor: colors.primary[50],
          borderWidth: 1,
          borderColor: colors.primary[100],
          overflow: 'hidden',
        }}
      >
        <View className="flex-row items-center" style={{ gap: 16 }}>
          <View className="shrink-0">
            <Text style={{ color: colors.primary[700], fontSize: 40, fontWeight: '800', letterSpacing: -1, fontVariant: ['tabular-nums'] }}>
              {total}
            </Text>
          </View>
          <View className="flex-1">
            <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 16, fontWeight: '700' }}>
              {total === 1 ? 'item you can delete' : 'items you can delete'}
            </Text>
            <Text numberOfLines={2} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 19, marginTop: 2 }}>
              Across {categories.length} categories. Want a copy first? Export it from Settings.
            </Text>
          </View>
        </View>
      </Animated.View>

      <Animated.View
        entering={FadeInDown.delay(120).duration(350)}
        style={{
          borderRadius: 20,
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          overflow: 'hidden',
        }}
      >
        {categories.map((c, i) => {
          const Icon = ICONS[c.key];
          const n = counts[c.key] ?? 0;
          return (
            <AnimatedPressable
              key={`cat-${c.key}`}
              onPress={() => onOpen(c.key)}
              accessibilityRole="button"
              accessibilityLabel={`${c.label}, ${n} stored`}
              className="flex-row items-center"
              style={{
                gap: 14,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 64,
                borderTopWidth: i === 0 ? 0 : 1,
                borderTopColor: colors.border.light,
              }}
            >
              <View
                className="shrink-0 items-center justify-center"
                style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: colors.surface.secondary }}
              >
                <Icon size={19} color={n > 0 ? colors.primary[700] : colors.text.tertiary} />
              </View>
              <View className="flex-1">
                <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
                  {c.label}
                </Text>
                <Text numberOfLines={2} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18, marginTop: 1 }}>
                  {c.description}
                </Text>
              </View>
              <View className="shrink-0 flex-row items-center" style={{ gap: 6 }}>
                <View
                  style={{
                    minWidth: 32,
                    paddingHorizontal: 9,
                    paddingVertical: 3,
                    borderRadius: 999,
                    alignItems: 'center',
                    backgroundColor: n > 0 ? colors.primary[100] : colors.surface.secondary,
                  }}
                >
                  <Text numberOfLines={1} style={{ color: n > 0 ? colors.primary[700] : colors.text.tertiary, fontSize: 13, fontWeight: '700', fontVariant: ['tabular-nums'] }}>
                    {c.mode === 'whole' && c.key === 'avatar' ? (n > 0 ? 'Yes' : '—') : n}
                  </Text>
                </View>
                <ChevronRight size={18} color={colors.text.tertiary} />
              </View>
            </AnimatedPressable>
          );
        })}
      </Animated.View>

      {companyLine && (
        <Animated.View entering={FadeInDown.delay(150).duration(350)}>
          <CompanyDataCard line={companyLine} onRefresh={() => onRefreshCompanyData(companyLine.href)} />
        </Animated.View>
      )}

      {teamCompanies.length > 0 && (
        <Animated.View
          entering={FadeInDown.delay(180).duration(350)}
          style={{
            borderRadius: 20,
            padding: 16,
            gap: 8,
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View className="flex-row items-center" style={{ gap: 10 }}>
            <View className="shrink-0">
              <Users size={18} color={colors.text.secondary} />
            </View>
            <Text numberOfLines={1} className="flex-1" style={{ color: colors.text.primary, fontSize: 15, fontWeight: '700' }}>
              Team data isn't listed here
            </Text>
          </View>
          <Text numberOfLines={5} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 19 }}>
            {teamCompanies.map((t) => t.name).join(', ')} {teamCompanies.length === 1 ? 'is' : 'are'} run with co-founders, so
            {teamCompanies.length === 1 ? ' its' : ' their'} documents belong to the team, not to you alone. Manage them in the
            data room, where the whole team can see what changes.
          </Text>
        </Animated.View>
      )}

      <AccountActionButton variant="secondary" label="Export a copy of my data" onPress={onExport} />
    </>
  );
}

// -----------------------------------------------------------------------------
// Company data — read-only
// -----------------------------------------------------------------------------

/**
 * Interview transcripts are company data: shown so the person knows they exist,
 * never offered for deletion here. The only action is the existing interview.
 */
function CompanyDataCard({ line, onRefresh }: { line: CompanyDataLine; onRefresh: () => void }) {
  const colors = useThemeColors();
  return (
    <View
      testID="manage-data-company-data"
      style={{
        borderRadius: 20,
        padding: 16,
        gap: 10,
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
        overflow: 'hidden',
      }}
    >
      {/* Header row: the label truncates, the action never shrinks below 44pt. */}
      <View className="flex-row items-center" style={{ gap: 12 }}>
        <Text
          numberOfLines={1}
          className="flex-1"
          style={{ color: colors.text.tertiary, fontSize: 12, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase' }}
        >
          Company data
        </Text>
        {/* pressable_sizing: the View owns the 44pt target; the pressable fills it. */}
        <View
          className="shrink-0"
          style={{
            height: 44,
            borderRadius: 12,
            overflow: 'hidden',
            backgroundColor: colors.primary[50],
            borderWidth: 1,
            borderColor: colors.primary[100],
          }}
        >
          <AnimatedPressable
            onPress={onRefresh}
            accessibilityRole="link"
            accessibilityLabel={`${line.actionLabel} ${line.title.toLowerCase()} with a new interview`}
            className="flex-row items-center justify-center px-3"
            style={{ flex: 1, minHeight: 44, gap: 6 }}
          >
            <RefreshCw size={15} color={colors.primary[700]} />
            <Text numberOfLines={1} style={{ color: colors.primary[700], fontSize: 14, fontWeight: '700' }}>
              {line.actionLabel}
            </Text>
          </AnimatedPressable>
        </View>
      </View>
      <View className="flex-row items-start" style={{ gap: 14 }}>
        <View
          className="shrink-0 items-center justify-center"
          style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: colors.surface.secondary }}
        >
          <Mic size={19} color={colors.text.secondary} />
        </View>
        <View className="flex-1">
          <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
            {line.title}
          </Text>
          <Text numberOfLines={4} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18, marginTop: 1 }}>
            {line.body}
          </Text>
        </View>
      </View>
    </View>
  );
}

// -----------------------------------------------------------------------------
// One category
// -----------------------------------------------------------------------------

function CategoryDetail({
  meta,
  count,
  items,
  loading,
  error,
  onRetry,
  onDelete,
  onDeleteAll,
  analyticsOptOut,
  onToggleAnalytics,
  togglePending,
  showInlineBack,
  onBack,
  deleting,
}: {
  meta: CategoryMeta;
  count: number;
  items: DataItem[] | undefined;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  onDelete: (item: DataItem) => void;
  onDeleteAll: () => void;
  analyticsOptOut: boolean;
  onToggleAnalytics: (v: boolean) => void;
  togglePending: boolean;
  showInlineBack: boolean;
  onBack: () => void;
  deleting: boolean;
}) {
  const colors = useThemeColors();
  const Icon = ICONS[meta.key];

  return (
    <Animated.View entering={FadeIn.duration(250)} style={{ gap: 18 }}>
      {showInlineBack && (
        <View style={{ alignSelf: 'flex-start', height: 36, borderRadius: 10, overflow: 'hidden' }}>
          <AnimatedPressable
            onPress={onBack}
            accessibilityRole="button"
            accessibilityLabel="Back to all your data"
            className="flex-row items-center px-2"
            style={{ flex: 1, minHeight: 36, gap: 4 }}
          >
            <ChevronLeft size={18} color={colors.primary[700]} />
            <Text numberOfLines={1} style={{ color: colors.primary[700], fontSize: 14, fontWeight: '600' }}>
              All your data
            </Text>
          </AnimatedPressable>
        </View>
      )}

      <View className="flex-row items-start" style={{ gap: 14 }}>
        <View
          className="shrink-0 items-center justify-center"
          style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: colors.primary[50] }}
        >
          <Icon size={24} color={colors.primary[700]} />
        </View>
        <View className="flex-1">
          <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 24, fontWeight: '700', letterSpacing: -0.4 }}>
            {meta.label}
          </Text>
          <Text numberOfLines={4} style={{ color: colors.text.secondary, fontSize: 14, lineHeight: 20, marginTop: 4 }}>
            {meta.description}
          </Text>
        </View>
      </View>

      {meta.key === 'analytics_events' && (
        <View
          className="flex-row items-center"
          style={{
            gap: 12,
            borderRadius: 16,
            padding: 16,
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View className="flex-1">
            <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
              Collect usage analytics
            </Text>
            <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18, marginTop: 2 }}>
              {analyticsOptOut
                ? 'Off. Nothing about how you use the app is recorded.'
                : 'On. Turn it off and nothing new is recorded, on any device.'}
            </Text>
          </View>
          <View className="shrink-0">
            <Switch
              value={!analyticsOptOut}
              disabled={togglePending}
              onValueChange={(on) => onToggleAnalytics(!on)}
              trackColor={{ false: colors.border.medium, true: colors.primary[600] }}
              thumbColor="white"
              accessibilityRole="switch"
              accessibilityLabel="Collect usage analytics"
              accessibilityState={{ checked: !analyticsOptOut, disabled: togglePending }}
            />
          </View>
        </View>
      )}

      {meta.mode === 'whole' ? (
        <WholeCategory meta={meta} count={count} onDelete={onDeleteAll} deleting={deleting} />
      ) : loading ? (
        <View className="items-center justify-center" style={{ paddingVertical: 48 }}>
          <ActivityIndicator color={colors.primary[600]} />
        </View>
      ) : error ? (
        <Notice
          title="Couldn't load this list"
          body={error instanceof ManageDataError ? error.userMessage : 'Check your connection and try again.'}
          action={{ label: 'Try again', onPress: onRetry }}
        />
      ) : !items || items.length === 0 ? (
        <Notice title="Nothing stored" body={`You have no ${meta.label.toLowerCase()} to delete.`} />
      ) : (
        <>
          <Animated.View
            layout={LinearTransition.duration(220)}
            style={{
              borderRadius: 20,
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              overflow: 'hidden',
            }}
          >
            {items.map((item, i) => (
              <ItemRow key={`item-${item.id}`} item={item} first={i === 0} onDelete={() => onDelete(item)} disabled={deleting} />
            ))}
          </Animated.View>
          {count > items.length && (
            <Text numberOfLines={2} style={{ color: colors.text.tertiary, fontSize: 13 }}>
              Showing the newest {items.length} of {count}. Delete all removes every one.
            </Text>
          )}
          <AccountActionButton
            variant="destructive"
            label={`Delete all ${count > 0 ? count : items.length}`}
            icon={<Trash2 size={18} color={colors.text.inverse} />}
            disabled={deleting}
            onPress={onDeleteAll}
            accessibilityHint="Asks you to confirm twice before deleting everything in this category"
          />
        </>
      )}
    </Animated.View>
  );
}

function ItemRow({ item, first, onDelete, disabled }: { item: DataItem; first: boolean; onDelete: () => void; disabled: boolean }) {
  const colors = useThemeColors();
  const date = formatItemDate(item.createdAt);
  const meta = [item.subtitle, date].filter(Boolean).join(' · ');
  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(180)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingLeft: 16,
        paddingRight: 8,
        paddingVertical: 10,
        minHeight: 60,
        borderTopWidth: first ? 0 : 1,
        borderTopColor: colors.border.light,
      }}
    >
      <View className="flex-1">
        <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
          {item.title}
        </Text>
        {meta ? (
          <Text numberOfLines={1} style={{ color: colors.text.secondary, fontSize: 13, marginTop: 2 }}>
            {meta}
          </Text>
        ) : null}
      </View>
      {item.detail ? (
        <View
          className="shrink-0"
          style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: colors.surface.secondary, maxWidth: 110 }}
        >
          <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 12, fontWeight: '600' }}>
            {item.detail}
          </Text>
        </View>
      ) : null}
      {/* pressable_sizing: the View owns the 44pt target; the pressable fills it. */}
      <View className="shrink-0" style={{ width: 44, height: 44, borderRadius: 12, overflow: 'hidden' }}>
        <AnimatedPressable
          onPress={onDelete}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={`Delete ${item.title}`}
          className="items-center justify-center"
          style={{ flex: 1, minHeight: 44 }}
        >
          <Trash2 size={18} color={colors.error} />
        </AnimatedPressable>
      </View>
    </Animated.View>
  );
}

function WholeCategory({
  meta,
  count,
  onDelete,
  deleting,
}: {
  meta: CategoryMeta;
  count: number;
  onDelete: () => void;
  deleting: boolean;
}) {
  const colors = useThemeColors();
  const empty = count === 0;
  const line =
    meta.key === 'avatar'
      ? empty
        ? 'No photo is stored.'
        : 'Your photo and any earlier uploads are stored.'
      : empty
        ? 'No usage history is stored.'
        : `${count} recorded ${count === 1 ? 'event' : 'events'}.`;
  return (
    <View
      style={{
        borderRadius: 20,
        padding: 20,
        gap: 14,
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 16, fontWeight: '600' }}>
        {line}
      </Text>
      <AccountActionButton
        variant="destructive"
        label={meta.key === 'avatar' ? 'Delete my photo' : 'Delete my usage history'}
        icon={<Trash2 size={18} color={colors.text.inverse} />}
        disabled={empty || deleting}
        loading={deleting}
        onPress={onDelete}
      />
    </View>
  );
}

function Notice({ title, body, action }: { title: string; body: string; action?: { label: string; onPress: () => void } }) {
  const colors = useThemeColors();
  return (
    <View
      style={{
        borderRadius: 20,
        padding: 20,
        gap: 12,
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 17, fontWeight: '700' }}>
        {title}
      </Text>
      <Text numberOfLines={5} style={{ color: colors.text.secondary, fontSize: 14, lineHeight: 20 }}>
        {body}
      </Text>
      {action && <AccountActionButton variant="secondary" label={action.label} onPress={action.onPress} />}
    </View>
  );
}
